import React from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
// formik
import { Formik } from 'formik';
import * as Yup from 'yup';
import TextField from '@material-ui/core/TextField';
import withStyles from '@material-ui/core/styles/withStyles';
import extendedTablesStyle from 'assets/jss/material-dashboard-pro-react/views/extendedTablesStyle';
import GridContainer from 'components/UI/Grid/GridContainer';
import WithPermission from 'components/User/WithPermission';
import GridItem from 'components/UI/Grid/GridItem';
import Button from 'components/UI/CustomButtons/Button';
import sweetAlertStyle from 'assets/jss/material-dashboard-pro-react/views/sweetAlertStyle';
import Snackbars from 'components/UI/Snackbar/Snackbar';

const translateValidationSchema = t =>
  Yup.object({
    email: Yup.string().required(t('errors.required')),
    notificationTypeName: Yup.string().required(t('errors.required')),
    payload: Yup.string()
      .required(t('errors.required'))
      .test('is-json', t('errors.invalid_json'), value => {
        if (!value) return true;
        try {
          JSON.parse(value);
          return true;
        } catch (e) {
          return false;
        }
      }),
  });

/**
 * Initial values for SendPushNotification form.
 */
const initialValues = {
  email: '',
  notificationTypeName: '',
  payload: '',
};

/**
 * @class
 * @extends React.Component
 */
class SendPushNotification extends React.Component {
  constructor(props) {
    super(props);
    this.formikRef = null;
    this.state = {
      fields: {
        ...initialValues,
      },
    };
  }

  /**
   * @description
   * Set server errors.
   *
   * @param {Object} serverErrors - Server errors.
   */
  setServerErrors = serverErrors => {
    let all = null;
    if (this.formikRef && serverErrors.length) {
      const errors = {};

      serverErrors.forEach(item => {
        errors[item.field] = item.messages;
        if (item.field === '__all__') {
          all = item.messages;
        }
      });

      setTimeout(() => this.formikRef && this.formikRef.setErrors(errors), 0);
    }
    return all;
  };

  render() {
    const { fields } = this.state;

    const {
      classes,
      t,
      sendPushNotification,
      errors: serverErrors,
      success,
      closeNotification,
    } = this.props;
    const all = this.setServerErrors(serverErrors);
    const validationSchema = translateValidationSchema(t);
    return (
      <React.Fragment>
        <Snackbars
          place="tc"
          message={all || ''}
          open={!!all}
          color="danger"
          closeNotification={() => closeNotification()}
          close
        />
        <Snackbars
          place="tc"
          message={t('send_push_notification.success')}
          open={success}
          color="success"
          closeNotification={() => closeNotification()}
          close
        />
        <div className={classes.pageContainer}>
          <h3 className={classes.title}>{t('send_push_notification.title')}</h3>
          <div className={classes.contentContainer}>
            <GridContainer alignItems="center" justify="center">
              <GridItem xs={12} sm={6}>
                <WithPermission allowPermissions={[]}>
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '10px',
                    }}
                  >
                    <Formik
                      ref={ref => {
                        this.formikRef = ref;
                      }}
                      initialValues={fields}
                      validationSchema={validationSchema}
                      render={({
                        values,
                        errors,
                        touched,
                        handleChange,
                        handleBlur,
                      }) => (
                        <form>
                          <div
                            className={classes.adFormContainer}
                            style={{
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              gap: 16,
                            }}
                          >
                            <TextField
                              label={t('send_push_notification.email')}
                              name="email"
                              error={touched.email && !!errors.email}
                              helperText={touched.email ? errors.email : ''}
                              InputProps={{
                                value: values.email,
                                onChange: e => handleChange(e),
                                onBlur: handleBlur,
                                type: 'email',
                              }}
                            />
                            <TextField
                              label={t(
                                'send_push_notification.notificationTypeName',
                              )}
                              name="notificationTypeName"
                              error={
                                touched.notificationTypeName &&
                                !!errors.notificationTypeName
                              }
                              helperText={
                                touched.notificationTypeName
                                  ? errors.notificationTypeName
                                  : ''
                              }
                              InputProps={{
                                value: values.notificationTypeName,
                                onChange: e => handleChange(e),
                                onBlur: handleBlur,
                                type: 'text',
                              }}
                            />
                            <TextField
                              label={t('send_push_notification.payload')}
                              name="payload"
                              error={touched.payload && !!errors.payload}
                              helperText={touched.payload ? errors.payload : ''}
                              multiline
                              rows={15}
                              fullWidth
                              InputProps={{
                                value: values.payload,
                                onChange: e => handleChange(e),
                                onBlur: handleBlur,
                                type: 'text',
                              }}
                            />
                            <Button
                              simple
                              context-test="cache-key"
                              color="primary"
                              disabled={
                                !values.email ||
                                !values.notificationTypeName ||
                                !values.payload ||
                                Object.keys(errors).length > 0
                              }
                              onClick={() => sendPushNotification(values)}
                            >
                              {t('send_push_notification.btn')}
                            </Button>
                          </div>
                        </form>
                      )}
                    />
                  </div>
                </WithPermission>
              </GridItem>
            </GridContainer>
          </div>
        </div>
      </React.Fragment>
    );
  }
}

SendPushNotification.propTypes = {
  classes: PropTypes.instanceOf(Object).isRequired,
  t: PropTypes.func.isRequired,
  sendPushNotification: PropTypes.func.isRequired,
  errors: PropTypes.instanceOf(Array).isRequired,
  success: PropTypes.bool.isRequired,
  closeNotification: PropTypes.func.isRequired,
};

export default withTranslation()(
  withStyles({ ...extendedTablesStyle, ...sweetAlertStyle })(
    SendPushNotification,
  ),
);
